import React, { useState, useEffect, useLayoutEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, ToastBar } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { QueryManagerProvider } from './contexts/QueryManagerContext';
// Eager imports: the public auth/landing path + route guards + app shell. These
// must be in the initial bundle so the signin page paints immediately without
// waiting on the heavy authenticated app code.
import ProtectedRoute from './components/Layout/ProtectedRoute';
import PartnerRoute from './components/Layout/PartnerRoute';
import LandingPage from './components/Welcome/LandingPage';
import DesktopChrome from './components/Desktop/DesktopChrome';
import OpenAiConversionBeacon from './components/OpenAiConversionBeacon';
import SessionTimeout from './components/Auth/SessionTimeout';
import { isDemoMode } from './utils/demoDataGenerator';
import { dataIntegrationService } from './services/dataIntegrationService';
import { navigateOrRedirect } from './utils/desktopNavigation';
import './index.css';

// Lazy imports: every heavy leaf route (dashboards, product apps, analytics,
// admin, onboarding, integrations, OAuth callbacks, embeds). Each becomes its
// own chunk fetched on demand, so they no longer bloat the initial download.
const PartnerProgramLanding = lazy(() => import('./components/Welcome/PartnerProgramLanding'));
const PartnerPortal = lazy(() => import('./components/PartnerPortal/PartnerPortal'));
const CRMSetup = lazy(() => import('./components/Onboarding/CRMSetup'));
const DemoSetup = lazy(() => import('./components/Demo/DemoSetup'));
const ModernIntelligenceNavigator = lazy(() => import('./components/Dashboard/ModernIntelligenceNavigator'));
const InteractiveDashboard = lazy(() => import('./components/Dashboard/InteractiveDashboard'));
const ConditionalDashboard = lazy(() => import('./components/Dashboard/ConditionalDashboard'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const CompanySettings = lazy(() => import('./components/Dashboard/Settings/CompanySettings'));
const SubscriptionManagement = lazy(() => import('./components/Dashboard/SubscriptionManagement'));
const OnboardingRouter = lazy(() => import('./components/Onboarding/OnboardingRouter'));
const OnboardingQuestions = lazy(() => import('./components/Onboarding/OnboardingQuestions'));
const CRMIntegration = lazy(() => import('./components/Integration/CRMIntegration'));
const CSVUpload = lazy(() => import('./components/Integration/CSVUpload'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const OwnerUsage = lazy(() => import('./components/Owner/OwnerUsage'));
const ResetPasswordPage = lazy(() => import('./components/Auth/ResetPasswordPage'));
const OkrPortalPage = lazy(() => import('./components/OKR/OkrPortalPage'));
const OAuthCallback = lazy(() => import('./components/Integration/OAuthCallback'));
const GoogleCallback = lazy(() => import('./components/OAuth/GoogleCallback'));
const OneDriveCallback = lazy(() => import('./components/OAuth/OneDriveCallback'));
const SalesforceCallback = lazy(() => import('./components/OAuth/SalesforceCallback'));
const GoogleBusinessCallback = lazy(() => import('./components/OAuth/GoogleBusinessCallback'));
const HubSpotCallback = lazy(() => import('./components/OAuth/HubSpotCallback'));
const SlackOAuthCallback = lazy(() => import('./components/OAuth/SlackOAuthCallback'));
const PipedriveCallback = lazy(() => import('./components/OAuth/PipedriveCallback'));
const QuickBooksCallback = lazy(() => import('./components/OAuth/QuickBooksCallback'));
const XeroCallback = lazy(() => import('./components/OAuth/XeroCallback'));
const ShopifyCallback = lazy(() => import('./components/OAuth/ShopifyCallback'));
const HighLevelCallback = lazy(() => import('./components/OAuth/HighLevelCallback'));
const FollowUpBossCallback = lazy(() => import('./components/OAuth/FollowUpBossCallback'));
const ProcoreCallback = lazy(() => import('./components/OAuth/ProcoreCallback'));
const AssistantEmbed = lazy(() => import('./components/Embed/AssistantEmbed'));
const AgentEmbed = lazy(() => import('./components/Embed/AgentEmbed'));
const SharedDashboardView = lazy(() => import('./components/Workspace/SharedDashboardView'));
const EmailAssistantPlugin = lazy(() => import('./components/Plugin/EmailAssistantPlugin'));
const AcceptInvitation = lazy(() => import('./components/Invite/AcceptInvitation'));
const AutopilotApp = lazy(() => import('./components/Product/AutopilotApp'));
const DataIQApp = lazy(() => import('./components/Product/DataIQApp'));
const KnowledgeIQApp = lazy(() => import('./components/Product/KnowledgeIQApp'));

const DASHBOARD_TOAST_DISABLED_PREFIXES = [
  '/dashboard',
  '/full-dashboard',
  '/legacy-dashboard',
  '/modern-dashboard',
  '/settings',
  '/subscription',
];

const THEME_ENABLED_PREFIXES = [
  '/dashboard',
  '/full-dashboard',
  '/legacy-dashboard',
  '/modern-dashboard',
  '/settings',
  '/subscription',
];

let themeSwitchTimer = null;

const markThemeSwitching = () => {
  document.body.classList.add('xph-theme-switching');
  if (themeSwitchTimer) {
    window.clearTimeout(themeSwitchTimer);
  }
  themeSwitchTimer = window.setTimeout(() => {
    document.body.classList.remove('xph-theme-switching');
    themeSwitchTimer = null;
  }, 220);
};

const applyDashboardThemeToBody = (pathname, { animate = false } = {}) => {
  const isThemeEnabledRoute = THEME_ENABLED_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );
  if (!isThemeEnabledRoute) {
    document.body.classList.remove('xph-dashboard-theme-light');
    document.documentElement.classList.remove('xph-dashboard-theme-light');
    document.documentElement.style.colorScheme = 'dark';
    return;
  }
  const theme = localStorage.getItem('xph_dashboard_theme') || 'dark';
  const isLight = theme === 'light';
  if (animate) {
    markThemeSwitching();
  }
  document.body.classList.toggle('xph-dashboard-theme-light', isLight);
  document.documentElement.classList.toggle('xph-dashboard-theme-light', isLight);
  document.documentElement.style.colorScheme = isLight ? 'light' : 'dark';
};

function GlobalToaster() {
  const location = useLocation();
  const suppressDashboardToasts = DASHBOARD_TOAST_DISABLED_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 1,
        style: {
          background: '#1e293b',
          color: '#fff',
          border: '1px solid #334155',
        },
        success: {
          duration: 1,
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
        },
        loading: {
          duration: 1,
        },
        error: {
          duration: 4500,
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fff',
          },
        },
      }}
    >
      {(t) => {
        // Dashboard UX: suppress all toast popups for end users.
        if (suppressDashboardToasts) {
          return null;
        }
        return <ToastBar toast={t} />;
      }}
    </Toaster>
  );
}

function DashboardThemeBridge() {
  const location = useLocation();
  const lastThemeRef = useRef(null);

  useLayoutEffect(() => {
    const getCurrentTheme = () => {
      const isThemeEnabledRoute = THEME_ENABLED_PREFIXES.some((prefix) =>
        location.pathname.startsWith(prefix)
      );
      if (!isThemeEnabledRoute) return null;
      return localStorage.getItem('xph_dashboard_theme') || 'dark';
    };

    const applyThemeClass = () => {
      const nextTheme = getCurrentTheme();
      const shouldAnimate = nextTheme !== null && lastThemeRef.current !== null && nextTheme !== lastThemeRef.current;
      applyDashboardThemeToBody(location.pathname, { animate: shouldAnimate });
      lastThemeRef.current = nextTheme;
    };

    applyThemeClass();
    window.addEventListener('storage', applyThemeClass);
    window.addEventListener('xph-dashboard-theme-change', applyThemeClass);

    return () => {
      window.removeEventListener('storage', applyThemeClass);
      window.removeEventListener('xph-dashboard-theme-change', applyThemeClass);
    };
  }, [location.pathname]);

  return null;
}


// Minimal full-viewport fallback shown while a lazy route chunk downloads.
// Kept intentionally tiny (no heavy deps) so it lives in the initial bundle and
// renders instantly on a dark background to avoid a flash.
function RouteFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          border: '3px solid rgba(148,163,184,0.3)',
          borderTopColor: '#818cf8',
          borderRadius: '50%',
          animation: 'xph-route-spin 0.8s linear infinite',
        }}
      />
      <style>{'@keyframes xph-route-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

function App() {
  const [demoMode, setDemoMode] = useState(false);
  const [showDemoSetup, setShowDemoSetup] = useState(false);

  useEffect(() => {
    // Check if demo mode is enabled
    const isDemo = isDemoMode();
    setDemoMode(isDemo);
    
    // Check URL parameters for demo mode
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true') {
      setShowDemoSetup(true);
    }
  }, []);

  // Demo setup handler
  const handleDemoSetup = () => {
    setShowDemoSetup(true);
  };

  const handleDemoComplete = () => {
    setShowDemoSetup(false);
    setDemoMode(true);
    window.history.replaceState({}, document.title, '/dashboard');
  };

  // Show demo setup if requested
  if (showDemoSetup) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <DemoSetup onComplete={handleDemoComplete} />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <QueryManagerProvider>
        <SessionTimeout>
          <OnboardingProvider>
          <Router>
            <div className="App">
              <DesktopChrome />
              <OpenAiConversionBeacon />
              <DashboardThemeBridge />
              <GlobalToaster />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* Public Route - Landing/Auth */}
            <Route 
              path="/" 
              element={<LandingPage onDemoRequest={handleDemoSetup} />} 
            />
            
            {/* Password Reset Route (Public) */}
            <Route 
              path="/reset-password" 
              element={<ResetPasswordPage />} 
            />

            {/* Affiliate / Partner Program marketing page (Public) */}
            {/* Reachable from the landing-page footer and direct shares;
                CTA buttons route visitors back to /?view=signup&affiliate=1
                which triggers auto-enroll after email verification. */}
            {/* Public read-only OKR portal (tokenized, no session) */}
            <Route
              path="/okr-portal/:token"
              element={<OkrPortalPage />}
            />

            <Route
              path="/affiliates"
              element={<PartnerProgramLanding />}
            />
            <Route
              path="/partners"
              element={<PartnerProgramLanding />}
            />
            <Route
              path="/referral-program"
              element={<PartnerProgramLanding />}
            />

            {/* Partner Portal (Authenticated, BUT NOT ProtectedRoute) */}
            {/* This is the standalone affiliate dashboard. ProtectedRoute
                enforces IN-specific trial/product checks that don't apply
                to pure partner accounts; PartnerRoute is a slim auth-only
                guard that allows both 'customer' and 'partner' types. */}
            <Route
              path="/partner-portal"
              element={
                <PartnerRoute>
                  <PartnerPortal />
                </PartnerRoute>
              }
            />

            {/* Team Invitation Route (Public) */}
            <Route 
              path="/invite/:token" 
              element={<AcceptInvitation />} 
            />
            
            {/* OAuth Callback Route */}
            <Route 
              path="/auth/callback/:platform" 
              element={<OAuthCallback />} 
            />
            
            {/* Google Sheets OAuth Callback */}
            <Route 
              path="/oauth/google/callback" 
              element={<GoogleCallback />} 
            />
            
            {/* OneDrive OAuth Callback */}
            <Route 
              path="/oauth/microsoft/callback" 
              element={<OneDriveCallback />} 
            />
            
            {/* Salesforce OAuth Callback */}
            <Route 
              path="/oauth/salesforce/callback" 
              element={<SalesforceCallback />} 
            />
            
            {/* Google Business OAuth Callback */}
            <Route 
              path="/oauth/google-business/callback" 
              element={<GoogleBusinessCallback />} 
            />
            
            {/* HubSpot OAuth Callback */}
            <Route 
              path="/oauth/hubspot/callback" 
              element={<HubSpotCallback />} 
            />

            {/* Slack OAuth Callback (popup) */}
            <Route
              path="/oauth/slack/callback"
              element={<SlackOAuthCallback />}
            />

            {/* Pipedrive OAuth Callback */}
            <Route 
              path="/oauth/pipedrive/callback" 
              element={<PipedriveCallback />} 
            />

            {/* QuickBooks OAuth Callback */}
            <Route 
              path="/oauth/quickbooks/callback" 
              element={<QuickBooksCallback />} 
            />
            <Route
              path="/oauth/xero/callback"
              element={<XeroCallback />}
            />

            {/* Shopify OAuth Callback */}
            <Route 
              path="/oauth/shopify/callback" 
              element={<ShopifyCallback />} 
            />

            {/* GoHighLevel OAuth Callback */}
            <Route
              path="/oauth/highlevel/callback"
              element={<HighLevelCallback />}
            />

            {/* Follow Up Boss OAuth Callback */}
            <Route
              path="/oauth/followupboss/callback"
              element={<FollowUpBossCallback />}
            />

            {/* Procore OAuth Callback */}
            <Route
              path="/oauth/procore/callback"
              element={<ProcoreCallback />}
            />
            
            {/* Public Embed Route - No Auth Required */}
            <Route 
              path="/embed/assistant/:assistantId" 
              element={<AssistantEmbed />} 
            />

            {/* Public Embed Route for Data IQ / Autopilot agents */}
            <Route
              path="/embed/agent/:agentId"
              element={<AgentEmbed />}
            />

            {/* Public read-only shared Intelligence Workspace dashboard */}
            <Route
              path="/shared/dashboard/:token"
              element={<SharedDashboardView />}
            />
            
            {/* Demo Route */}
            <Route 
              path="/demo" 
              element={<DemoSetup onComplete={handleDemoComplete} />} 
            />
            
            {/* Chrome Extension Plugin Route - Protected */}
            <Route 
              path="/email-assistant-plugin" 
              element={
                <ProtectedRoute requireProfile={false}>
                  <EmailAssistantPlugin />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <ConditionalDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Full Interactive Dashboard (for users with data) */}
            <Route 
              path="/full-dashboard" 
              element={
                <ProtectedRoute>
                  <InteractiveDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Legacy Dashboard Route */}
            <Route 
              path="/legacy-dashboard" 
              element={
                <ProtectedRoute>
                  <ModernIntelligenceNavigator />
                </ProtectedRoute>
              } 
            />

            {/* Modern Dashboard Route */}
            <Route 
              path="/modern-dashboard" 
              element={
                <ProtectedRoute>
                  <InteractiveDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Onboarding Routes - Don't require profile completion */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireProfile={false}>
                  <OnboardingRouter />
                </ProtectedRoute>
              }
            />

            {/* First-run signup questions (Supademo-style) — shown once per
                user right after the first authentication, before the dashboard */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute requireProfile={false}>
                  <OnboardingQuestions />
                </ProtectedRoute>
              }
            />

            <Route 
              path="/legacy-onboarding" 
              element={
                <ProtectedRoute>
                  <CRMSetup onComplete={() => navigateOrRedirect('/dashboard')} />
                </ProtectedRoute>
              } 
            />

            {/* Integration Routes */}
            <Route 
              path="/crm-integration" 
              element={
                <ProtectedRoute>
                  <CRMIntegration onComplete={() => navigateOrRedirect('/dashboard')} />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/data-upload" 
              element={
                <ProtectedRoute>
                  <CSVUpload onComplete={() => navigateOrRedirect('/dashboard')} />
                </ProtectedRoute>
              } 
            />

            {/* Settings Route */}
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />

            {/* Subscription Management Route */}
            <Route 
              path="/subscription" 
              element={
                <ProtectedRoute>
                  <SubscriptionManagement />
                </ProtectedRoute>
              } 
            />

            {/* Standalone Autopilot App Route - No profile required (no analytics module) */}
            <Route 
              path="/app/autopilot/*" 
              element={
                <ProtectedRoute requireProfile={false}>
                  <AutopilotApp />
                </ProtectedRoute>
              } 
            />

            {/* Standalone Data IQ App Route - No profile required (no analytics module) */}
            <Route 
              path="/app/data-iq/*" 
              element={
                <ProtectedRoute requireProfile={false}>
                  <DataIQApp />
                </ProtectedRoute>
              } 
            />

            {/* Standalone Knowledge IQ App Route - No profile required (no analytics module) */}
            <Route 
              path="/app/knowledge-iq/*" 
              element={
                <ProtectedRoute requireProfile={false}>
                  <KnowledgeIQApp />
                </ProtectedRoute>
              } 
            />

            {/* Company Settings Route */}
            <Route 
              path="/settings/company" 
              element={
                <ProtectedRoute>
                  <CompanySettings />
                </ProtectedRoute>
              } 
            />

            {/* Platform owner only. Not linked from the customer app. */}
            <Route
              path="/owner"
              element={
                <ProtectedRoute>
                  <OwnerUsage />
                </ProtectedRoute>
              }
            />

            {/* Admin Dashboard Route */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/admin/users/:userId" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </Suspense>
        </div>
      </Router>
          </OnboardingProvider>
        </SessionTimeout>
      </QueryManagerProvider>
    </AuthProvider>
  );
}

export default App;